import React from 'react';

import { studioTheme, STUDIO_MONO } from './studioTheme';
import {
  StudioWrap,
  SourceLogo,
  Stars,
  DateText,
  secondaryLine,
} from './StudioCardKit';
import { StudioBody, isMediaTestimonial, StudioMediaCard } from './StudioBody';
import { socialCardKeys } from '@/src/constants/socialcard.constant';

/**
 * Ink Block (widget type 14)
 *
 * Square corners, a two-pixel ink border and a hard offset shadow. The
 * attribution sits in mono under a heavy rule. Built for developer tools and
 * design brands where the loud, honest card reads as credible.
 */
const InkBlockCard = (props) => {
  const { cardItem, socialCardType } = props;
  const t = studioTheme(props);

  /* The ink is this card's whole identity, so it stays black/white until the
     customer picks an accent - then border, offset shadow and the rule under
     the attribution all follow it (the "Ink Color" control writes
     quotation_color). t.brand is not usable here: it falls back to purple. */
  const inkLine = t.inkColor || (t.dark ? '#f2f3f7' : '#15171c');
  const role = secondaryLine(cardItem);
  const link = cardItem && cardItem[socialCardKeys.messageLink];

  /* One body type scale per card: the text testimonial and the video/audio
     summary must read as the same widget. */
  const bodyTextStyle = { fontSize: t.smallSize ? 15.5 : 17, lineHeight: 1.3, letterSpacing: '-0.01em' };

  /* A video or audio testimonial IS the card - the player already carries
     the name and work title, so none of the chrome below is drawn. Only the
     summary keeps this widget's look, as the panel it extends into. */
  if (isMediaTestimonial(cardItem, socialCardType)) {
    return (
      <StudioMediaCard
        cardItem={cardItem}
        socialCardType={socialCardType}
        t={t}
        enclosure={{
          /* the ink border and offset shadow ARE this card, so wrap the player even with no summary. */
          always: true,
          border: `2px solid ${inkLine}`,
          borderRadius: 0,
          background: t.surface,
          /* the wall's custom card colour wins, as it does on the text card */
          ...(t.cardBackgroundObject || {}),
          boxShadow: `5px 5px 0 ${inkLine}`,
        }}
        summaryStyle={bodyTextStyle}
      />
    );
  }

  return (
    <StudioWrap>
      <div
        style={{
          position: 'relative',
          borderRadius: 0,
          padding: 18,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          border: `2px solid ${inkLine}`,
          background: t.surface,
          ...(t.cardBackgroundObject || {}),
          boxShadow: `5px 5px 0 ${inkLine}`,
        }}
      >

        <StudioBody
          t={t}
          cardItem={cardItem}
          socialCardType={socialCardType}
          mediaRadius={0}
          textStyle={bodyTextStyle}
        />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <Stars t={t} cardItem={cardItem} size={13} />
            <DateText t={t} cardItem={cardItem} size={11} />
          </span>
          <SourceLogo cardItem={cardItem} />
        </div>

        <div
          className={t.fontClass}
          style={{
            borderTop: `2px solid ${inkLine}`,
            paddingTop: 10,
            display: 'flex',
            justifyContent: 'space-between',
            gap: 8,
            fontFamily: t.fontStyle ? undefined : STUDIO_MONO,
            fontSize: 11,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            minWidth: 0,
          }}
        >
          <a
            href={link || undefined}
            target={link ? '_blank' : '_self'}
            rel="noreferrer noopener"
            style={{
              color: t.ink,
              fontWeight: 600,
              textDecoration: 'none',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {cardItem && cardItem[socialCardKeys.authorName]}
          </a>
          {role ? (
            <span
              style={{
                color: t.muted,
                textAlign: 'right',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {role}
            </span>
          ) : null}
        </div>
      </div>
    </StudioWrap>
  );
};

export default InkBlockCard;
